// ANÁLISE E MUDANÇAS:
// 1. LAYOUT DO CARD: Adicionadas classes ao `CardForm` para limitar sua largura em telas grandes (`max-w-4xl`) e centralizá-lo (`mx-auto`), atendendo ao pedido de redução de 30%. A classe `w-full` garante a responsividade em telas menores.
// 2. ESPAÇAMENTO DO FORMULÁRIO: Adicionada a classe `gap-y-4` ao grid do formulário para criar um espaçamento vertical consistente entre os campos.
// 3. PADRONIZAÇÃO DE FONTES: Aplicadas as classes `font-displayed` e `font-orator` nos títulos e labels para alinhar com o design system do projeto.

import { api } from '@common';
import {
  CardForm,
  InputMask,
  RadioGender,
  WrapperPage,
} from '@components/atoms';
import { maskUSPhone } from '@helpers';
import { useAuth } from '@hooks';
import {
  Button,
  DatePicker,
  Form,
  Input,
  message,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';


export const SettingsPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { profile } = useAuth();

  const [form] = Form.useForm();

  const { data, isLoading: isLoadingProfile, refetch } = profile;

  const updateProfile = (formData: any) => {
    setIsLoading(true);
    // Transforma o objeto dayjs de volta para uma string no formato YYYY-MM-DD antes de enviar
    const payload: any = {};
    
    // Only include fields that have values (not null, undefined, or empty string)
    if (formData.name) payload.name = formData.name;
    if (formData.phone) payload.phone = formData.phone;
    if (formData.gender) payload.gender = formData.gender;
    if (formData.profession) payload.profession = formData.profession;
    if (formData.nationality) payload.nationality = formData.nationality;
    if (formData.avatar) payload.avatar = formData.avatar;
    if (formData.birthday) {
      payload.birthday = formData.birthday.format('YYYY-MM-DD');
    }
    
    api
      .patch('/users/profile', payload)
      .then(() => {
        message.success('Profile updated');
        refetch();
      })
      .catch(error => {
        console.error('Error updating profile:', error);
        message.error('Failed to update profile');
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (!isLoadingProfile && data) {
      const formValues = {
        ...data?.physicalPerson,
        birthday: data?.physicalPerson?.birthday
          ? dayjs(data?.physicalPerson?.birthday) // dayjs vai parsear a string
          : null,
        phone: data?.physicalPerson?.phone ? maskUSPhone(data?.physicalPerson?.phone) : null,
      };
      form.setFieldsValue(formValues);
    }
  }, [data, isLoadingProfile, form]);

  return (
    <div className="bg-[url(/assets/images/Background.png)] bg-center bg-cover bg-no-repeat pt-25 pb-10 md:pb-20 justify-center">
      <WrapperPage isLoading={isLoading || isLoadingProfile}>
        <div className="container h-full flex flex-col justify-center items-center mx-auto">
          <div className="font-displayed !font-normal text-orangicide text-6xl tracking-wider pb-24"
          >SETTINGS
          </div>
        </div>

        <CardForm className="w-full max-w-4xl mx-auto">
          <Typography.Title level={2} className='font-displayed !font-normal text-orangicide'>Personal Information</Typography.Title>

          <Form
            size='large'
            layout='vertical'
            form={form}
            onFinish={updateProfile}
            className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4'
          >
            <Form.Item label={<span className="font-orator text-white">Name</span>} name='name' rules={[{ required: true, message: "please enter your name" }]}>
              <Input placeholder='enter name' />
            </Form.Item>

            <Form.Item label={<span className="font-orator text-white">Date of Birth</span>} name='birthday'>
              <DatePicker
                className='w-full'
                format="MM/DD/YYYY"
                placeholder='MM/DD/YYYY'
              />
            </Form.Item>

            <Form.Item label={<span className="font-orator text-white">Phone</span>} name='phone' >
              <InputMask
                mask="usphone" placeholder='(000) 000-0000' />
            </Form.Item>

            <Form.Item
              label={<span className="font-orator text-white">Gender</span>}
              name='gender'
            >
              <RadioGender />
            </Form.Item>

            <Form.Item label={<span className="font-orator text-white">Profession</span>} name='profession'>
              <Input placeholder='enter profession' />
            </Form.Item>

            <div className='col-span-1 md:col-span-2 flex justify-center my-4'>
              <Button className='font-displayed !font-normal !text-3xl uppercase btn-orangicide w-80 !h-14' htmlType='submit'>
                Update Profile
              </Button>
            </div>
          </Form>
        </CardForm>
      </WrapperPage>
    </div>
  );
};
